import { useFilters } from '@hooks/data/useFilters';
import { TCategory } from '@api/io/categories.io';
import { useMemo } from 'react';
import { useConfigContext } from '@fe-core';

export type MappedCategoryData = {
  categories?: TCategory[] | null;
  selectedCategory?: TCategory | null;
  flatCategories: Map<number, TCategory>;
};

const mapCategories = (
  availableCategories: TCategory[],
  selectedCategoryId: number | null,
): MappedCategoryData => {
  const flatCategories = new Map<number, TCategory>();
  const flattenCategories = (category: TCategory) => {
    flatCategories.set(category.id, category);
    category.children.forEach(flattenCategories);
  };

  availableCategories?.forEach(flattenCategories);
  const selectedCategory: TCategory | undefined = flatCategories.get(
    selectedCategoryId || -1,
  );

  const categories: TCategory[] | undefined | null = selectedCategory
    ? (selectedCategory.children as TCategory[])
    : availableCategories;

  return {
    categories,
    selectedCategory,
    flatCategories,
  };
};

export const useFilterCategoryData = ({
  selectedCategoryId,
}: {
  selectedCategoryId: number | null;
}) => {
  const { config } = useConfigContext();
  const {
    data: filterCategories,
    isFetching,
    isError,
    error,
  } = useFilters({ enabled: !!config.loggedIn });

  const categoryData = useMemo(() => {
    if (!filterCategories) {
      return null;
    }

    return mapCategories(filterCategories.categories, selectedCategoryId);
  }, [filterCategories, selectedCategoryId]);

  const usableFilters = new Set(
    filterCategories?.filters.map((filter) => filter.name),
  );

  return { categoryData, usableFilters, isFetching, isError, error };
};
